import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

// Define interfaces for API responses for better type safety
interface LoginResponse {
  token?: string;
  message?: string;
  users?: { id: number; name: string }[]; // For multiple LC selection
  require_otp?: boolean;
  require_totp_setup?: boolean;
  totp_secret?: string;
  totp_url?: string; // URL for QR code image
  error?: string;
  two_factor_enabled?: boolean;
  device_token?: string;
  user_id?: number; // Added for new logic
  success?: boolean; // Added for trusted device responses
  trusted_device?: boolean; // Added for trusted device flag
}

// Add this helper function to hash email/username
export async function hashIdentifier(identifier: string, password?: string): Promise<string> {
  // If password is provided, create a combined identifier
  const combinedIdentifier = password ? `${identifier.toLowerCase().trim()}:${password}` : identifier.toLowerCase().trim();
  const encoder = new TextEncoder();
  const data = encoder.encode(combinedIdentifier);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}

@Injectable({
  providedIn: 'root' // This service will be provided at the root level
})
export class AuthService {
  // Base URL for your CakePHP APIs. Adjust as per your backend setup.
  private apiUrl = '/auth';
  // URL where the user should be redirected after successful login/2FA.
  private redirectUrl = '/auth/token-login';

  constructor(private http: HttpClient, private router: Router) { }

  /**
   * Handles the initial login request.
   * @param credentials User's email/username and password, optionally network_id.
   * @returns An Observable of LoginResponse.
   */
  async login(credentials: { email?: string; password?: string; network_id?: number }): Promise<LoginResponse> {
    let deviceToken;
    const trustedDevices = JSON.parse(localStorage.getItem('trustedDeviceTokens') || '{}');
    let key;
    if (credentials.email) {
      key = await hashIdentifier(credentials.email);
      if (trustedDevices[key] && Array.isArray(trustedDevices[key]) && trustedDevices[key].length > 0) {
        // Use the first device token (or could implement device selection logic)
        deviceToken = trustedDevices[key][0];
      }
    }
    const payload = { 
      ...credentials,
      login_ui_type: 'new-login' // Indicate this is from the new login UI
    };
    if (deviceToken) {
      payload['device_token'] = deviceToken;
    }
    return this.http.post<LoginResponse>(`${this.apiUrl}/api-login`, payload).toPromise().then(response => {
      console.log('AuthService - API response received:', response);
      if (response.token && !response.require_otp && !response.require_totp_setup) {
        console.log('AuthService - Calling handleSuccessfulLogin with token');
        this.handleSuccessfulLogin(response.token);
      }
      // If login response includes user_id and email, move token from email hash to user_id hash if needed
      if (response.token && response.user_id && credentials.email && trustedDevices[key]) {
        trustedDevices[key] = trustedDevices[key];
        localStorage.setItem('trustedDeviceTokens', JSON.stringify(trustedDevices));
      }
      return response;
    }).catch(error => {
      console.error('Login API error:', error);
      console.error('Full API error details:', JSON.stringify(error, null, 2));
      return { message: error.error?.message || 'Login error. Please try again.' };
    });
  }

  /**
   * Verifies the One-Time Password (OTP) provided by the user.
   * @param token The temporary token received after initial login.
   * @param otp The 6-digit OTP code.
   * @param remember_device Whether to trust this device for future logins.
   * @returns An Observable of LoginResponse.
   */
  async verifyOtp(token: string, otp: string, remember_device: boolean = false, email?: string, password?: string, deviceToken?: string): Promise<LoginResponse> {
    const body: any = { token, otp, remember_device };
    if (deviceToken) body.device_token = deviceToken;
    return this.http.post<LoginResponse>(`${this.apiUrl}/otp-verification`, body).toPromise().then(async response => {
      if (response.token) {
        this.handleSuccessfulLogin(response.token);
      }
      if (remember_device && response.device_token && email) {
        const trustedDevices = JSON.parse(localStorage.getItem('trustedDeviceTokens') || '{}');
        const key = await hashIdentifier(email);
        
        // Initialize as array if not exists
        if (!trustedDevices[key]) {
          trustedDevices[key] = [];
        } else if (!Array.isArray(trustedDevices[key])) {
          // Convert existing single token to array for backward compatibility
          trustedDevices[key] = [trustedDevices[key]];
        }
        
        // Add new device token if not already present
        if (!trustedDevices[key].includes(response.device_token)) {
          trustedDevices[key].push(response.device_token);
        }
        
        localStorage.setItem('trustedDeviceTokens', JSON.stringify(trustedDevices));
      }
      return response;
    }).catch(error => {
      console.error('OTP verification API error:', error);
      return { error: error.error?.error || 'Verification failed. Try again.' };
    });
  }

  /**
   * Completes the TOTP setup by verifying the initial code after scanning/entering secret.
   * @param token The temporary token received after initial login.
   * @param otp The 6-digit OTP code from the authenticator app.
   * @returns An Observable of LoginResponse.
   */
  setupTotp(token: string, otp: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/totp-setup`, { token, otp }).pipe(
      tap(response => {
        if (response.token) {
          this.handleSuccessfulLogin(response.token);
        }
      }),
      catchError(error => {
        console.error('TOTP setup API error:', error);
        return of({ error: error.error?.error || 'Setup failed. Please try again.' });
      })
    );
  }

  /**
   * Stores the authentication token and redirects the user.
   * @param token The final authentication token.
   */
  public handleSuccessfulLogin(token: string): void {
    console.log('AuthService - handleSuccessfulLogin called with token:', token);
    localStorage.setItem('lms_token', token);
    // Encode so +, /, = in the JWT cannot break the query string (decode fails → old signin).
    const redirectUrl = `${this.redirectUrl}?token=${encodeURIComponent(token)}`;
    console.log('AuthService - Redirecting to:', redirectUrl);
    // Redirect using window.location.href to ensure full page reload for token-based login
    window.location.href = redirectUrl;
  }

  /**
   * Checks user preference for the new login UI from local storage.
   * @returns True if new UI is preferred, false otherwise.
   */
  useNewLoginUI(): boolean {
    return localStorage.getItem('useNewLoginUI') === 'true';
  }

  /**
   * Sets user preference for the new login UI in local storage.
   * @param value Boolean indicating preference.
   */
  setUseNewLoginUI(value: boolean): void {
    localStorage.setItem('useNewLoginUI', value.toString());
  }

  /**
   * Sends a password reset request for the given email/username.
   * @param email The user's email or username.
   * @returns An Observable of the reset response.
   */
  resetPassword(email: string) {
    return this.http.post<any>(`${this.apiUrl}/sendResetPassword`, { username: email });
  }

  /**
   * Gets the authorized users for the current session (used for switch LC functionality)
   * @returns An Observable of LoginResponse containing the users array
   */
  getAuthorizedUsers(): Observable<LoginResponse> {
    return this.http.get<LoginResponse>(`${this.apiUrl}/get-authorized-users`).pipe(
      tap(response => console.log('AuthService - getAuthorizedUsers response:', response)),
      catchError(error => {
        console.error('AuthService - getAuthorizedUsers error:', error);
        return of({ error: 'Failed to get authorized users' });
      })
    );
  }

  /**
   * Gets the stored credentials from the session (used for switch LC functionality)
   * @returns An Observable containing the stored email, password, and optional OAuth provider
   */
  getStoredCredentials(): Observable<{ email: string; password: string; oauth_provider?: string }> {
    return this.http.get<{ email: string; password: string; oauth_provider?: string }>(`${this.apiUrl}/get-stored-credentials`).pipe(
      tap(response => console.log('AuthService - getStoredCredentials response:', response)),
      catchError(error => {
        console.error('AuthService - getStoredCredentials error:', error);
        return of({ email: '', password: '' });
      })
    );
  }

  /**
   * Clear all trusted devices for a user
   * Useful when localStorage is cleared and user wants to reset trusted devices
   */
  clearTrustedDevices(email?: string, username?: string, userId?: number): Observable<any> {
    const payload: any = {};
    if (email) payload.email = email;
    if (username) payload.username = username;
    if (userId) payload.user_id = userId;
    
    return this.http.post<any>(`${this.apiUrl}/clear-trusted-devices`, payload).pipe(
      tap(response => console.log('AuthService - clearTrustedDevices response:', response)),
      catchError(error => {
        console.error('AuthService - clearTrustedDevices error:', error);
        return of({ error: 'Failed to clear trusted devices' });
      })
    );
  }
}
